import safeFetch from "../utils/safeFetch"

const BASE = import.meta.env.VITE_API_URL || ""

function _capturarTokenDeURL() {
  if (typeof window === "undefined") return
  try {
    const params = new URLSearchParams(window.location.search)
    const t = params.get("t")
    if (!t) return
    localStorage.setItem("token", t)
    params.delete("t")
    const qs = params.toString()
    const newUrl = window.location.pathname + (qs ? "?" + qs : "")
    window.history.replaceState({}, "", newUrl)
  } catch (_) {}
}
_capturarTokenDeURL()

export function getToken() {
  return localStorage.getItem("token") || ""
}

export function setToken(t) {
  if (t) localStorage.setItem("token", t)
  else localStorage.removeItem("token")
}

async function req(path, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
    ...(options.headers || {})
  }
  const token = getToken()
  if (token) headers.Authorization = "Bearer " + token

  const fetchFn = safeFetch || window.fetch
  // FIX: cache: "no-store" evita que el navegador guarde respuestas de la API
  const res = await fetchFn(BASE + path, { ...options, headers, cache: "no-store" })
  const text = await res.text()
  let data = null
  try { data = text ? JSON.parse(text) : null } catch (_) { data = null }

  if (res.status === 401 && token) {
    const code = data && data.code
    const reason = code === "SESSION_REVOKED" ? "session_revoked" : "expired"
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
      setToken(null)
      localStorage.removeItem("user")
      window.location.href = "/login?reason=" + reason
    }
    return new Promise(() => {})
  }

  if (!res.ok) {
    const err = new Error((data && data.error) || ("HTTP " + res.status))
    if (data && data.code) err.code = data.code
    err.status = res.status
    throw err
  }
  return data
}

export const api = {
  get: (path) => req(path),
  post: (path, body) => req(path, { method: "POST", body: JSON.stringify(body) }),
  put: (path, body) => req(path, { method: "PUT", body: JSON.stringify(body) }),
  del: (path) => req(path, { method: "DELETE" }),
  patch: (path, body) => req(path, { method: "PATCH", body: JSON.stringify(body) }),
  myWallets: () => req("/api/wallets/me"),
  myLedger: () => req("/api/wallets/me/ledger"),
  services: () => req("/api/services"),
  myTopups: () => req("/api/topups/me"),
  createTopup: (b) => req("/api/topups", { method: "POST", body: JSON.stringify(b) }),
  sendTopupInstructions: (id) => req("/api/topups/" + id + "/send-instructions", { method: "POST" }),
  uploadVoucher: (id, imagenBase64) => req("/api/topups/" + id + "/voucher", { method: "POST", body: JSON.stringify({ imagenBase64 }) }),
  getVoucher: (id) => req("/api/topups/" + id + "/voucher"),
  adminGetVoucher: (id) => req("/api/admin/topups/" + id + "/voucher"),
  myFlows: () => req("/api/flows/me"),
  bioLoginStart: (b) => req("/api/auth/biometric/start", { method: "POST", body: JSON.stringify(b) }),
  adminMetrics: () => req("/api/admin/metrics"),
  adminUsers: () => req("/api/admin/users"),
  adminTopups: (status) => req("/api/admin/topups" + (status ? "?status=" + status : "")),
  bioLogin2DAbandon: (sessionUuid, reason) => req("/api/auth/biometric/abandon", {
    method: "POST",
    body: JSON.stringify({ sessionUuid, reason }),
  }),
  adminSecurityAttempts: (params) => {
    const qs = new URLSearchParams(params || {}).toString()
    return req("/api/admin/security/attempts" + (qs ? "?" + qs : ""))
  },
  adminSecurityDetail: (id) => req("/api/admin/security/attempts/" + id),
  adminSecurityReview: (id, decision, notes) => req(
    "/api/admin/security/attempts/" + id + "/review",
    { method: "POST", body: JSON.stringify({ decision, notes }) }
  ),
  adminApprove: (id) => req("/api/admin/topups/" + id + "/approve", { method: "POST" }),
  adminReject: (id, reason) => req("/api/admin/topups/" + id + "/reject", { method: "POST", body: JSON.stringify({ reason }) }),
  mpCheckout: (b) => req("/api/topups/mercadopago/checkout", { method: "POST", body: JSON.stringify(b) }),
  yapePay: (b) => req("/api/yape/pay", { method: "POST", body: JSON.stringify(b) }),
  mpCardPay: (b) => req("/api/payments/card", { method: "POST", body: JSON.stringify(b) }),
  mpTicketPay: (b) => req("/api/payments/ticket", { method: "POST", body: JSON.stringify(b) }),
  mySessions: () => req("/api/auth/me/sessions"),
  revokeSession: (uuid) => req("/api/auth/me/sessions/" + uuid + "/revoke", { method: "POST" }),
  myDevices: () => req("/api/auth/me/devices"),
  forgetDevice: (id) => req("/api/auth/me/devices/" + id + "/forget", { method: "POST" }),
  logoutBackend: () => req("/api/auth/logout", { method: "POST" }),
  getMe: () => req("/api/auth/me"),
  resendPhoneVerification: () => req("/api/register/phone/resend", { method: "POST" }),
  verifyPhoneOtpAuth: (otp) => req("/api/register/phone/verify-auth", { method: "POST", body: JSON.stringify({ otp }) }),
  myCompanies: () => req("/api/auth/me/companies"),
  switchCompany: (companyId) => req("/api/auth/switch-company", { method: "POST", body: JSON.stringify({ companyId }) }),
  createCompany: (data) => req("/api/companies/new", { method: "POST", body: JSON.stringify(data) }),
  myLoginHistory: (limit) => req("/api/auth/me/login-history" + (limit ? "?limit=" + limit : "")),
  getReniecSignature: (dni) => req("/api/biometric/reniec-signature/" + dni),
  verifyCertificate: (uuid) => req("/api/verify/" + uuid),
  getStatus: () => req("/api/status"),
  verifyInvoice: (hash) => req("/api/verify/public/invoice/hash/" + hash),
  adminVerifications: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return req("/api/admin/verifications" + (qs ? "?" + qs : ""))
  },
  adminVerificationsStats: () => req("/api/admin/verifications/stats"),
  adminForceRevalidate: (id) => req("/api/admin/verifications/" + id + "/force-revalidate", { method: "POST" }),
  adminUnblockVerification: (id) => req("/api/admin/verifications/" + id + "/unblock", { method: "POST" }),
  adminRevokeVerification: (id) => req("/api/admin/verifications/" + id, { method: "DELETE" }),
  adminVerificationTimeline: (id) => req("/api/admin/verifications/" + id + "/timeline"),
  adminRevalidationsSummary: () => req("/api/admin/revalidations/summary"),
  adminRevalidationsOverdue: (includeWeek = true) => req("/api/admin/revalidations/overdue?include_week=" + (includeWeek ? "true" : "false")),
  adminManualReviewList: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return req("/api/admin/manual-review" + (qs ? "?" + qs : ""))
  },
  adminManualReviewDetail: (tipo, id) => req(`/api/admin/manual-review/${tipo}/${id}`),
  adminManualReviewApprove: (tipo, id, notes) => req(
    `/api/admin/manual-review/${tipo}/${id}/approve`,
    { method: "POST", body: JSON.stringify({ notes: notes || "" }) }
  ),
  adminManualReviewReject: (tipo, id, notes) => req(
    `/api/admin/manual-review/${tipo}/${id}/reject`,
    { method: "POST", body: JSON.stringify({ notes: notes || "" }) }
  ),
  adminBulkRevalidate: (ids, force = false) => req("/api/admin/verifications/bulk-revalidate", {
    method: "POST",
    body: JSON.stringify({ ids, force }),
  }),
  adminWorkerHealth: () => req("/api/admin/worker-health"),
  adminCircuitBreaker: () => req("/api/admin/circuit-breaker"),
  adminCircuitBreakerReset: () => req("/api/admin/circuit-breaker/reset", { method: "POST" }),
  adminVerificationsCsvUrl: (params = {}) => {
    const qs = new URLSearchParams(params).toString()
    return "/api/admin/verifications/export.csv" + (qs ? "?" + qs : "")
  },
  getMyCompany: () => req("/api/companies/me"),
  upsertMyCompany: (b) => req("/api/companies/me", { method: "PUT", body: JSON.stringify(b) }),
  rotateWebhookSecret: () => req("/api/companies/me/rotate-webhook-secret", { method: "POST" }),
  lookupRuc: (ruc) => req("/api/companies/lookup-ruc", { method: "POST", body: JSON.stringify({ ruc }) }),
  transferCompany: (newDni) => req("/api/companies/me/transfer", { method: "POST", body: JSON.stringify({ newDni }) }),
  companyVerification: () => req("/api/companies/me/verification"),
  myBilling: (params) => {
    const qs = new URLSearchParams(params || {}).toString()
    return req("/api/companies/me/billing" + (qs ? "?" + qs : ""))
  },
  downloadInvoice: async (topupId) => {
    const token = getToken()
    const res = await fetch("/api/companies/me/billing/" + topupId + "/invoice", {
      headers: { Authorization: "Bearer " + token },
      cache: "no-store" // FIX: Desactiva caché
    })
    if (!res.ok) {
      let msg = "Error descargando recibo"
      try {
        const j = await res.json()
        msg = j.error || msg
      } catch (_) { }
      throw new Error(msg)
    }
    return res.blob()
  },
  listPlanes: () => req("/api/topups/plans"),
  bankInfo: () => req("/api/topups/bank-info"),
  createPlanOrder: (body) => req("/api/topups/plans/create", { method: "POST", body: JSON.stringify(body) }),
  previewPlanOrder: (body) => req("/api/topups/plans/preview", { method: "POST", body: JSON.stringify(body) }),
  previewPenOrder: (body) => req("/api/topups/pen/preview", { method: "POST", body: JSON.stringify(body) }),
  listWebhookDeliveries: (limit) => req("/api/companies/me/webhook-deliveries" + (limit ? "?limit=" + limit : "")),
  getWebhookDelivery: (id) => req("/api/companies/me/webhook-deliveries/" + id),
  retryWebhookDelivery: (id) => req("/api/companies/me/webhook-deliveries/" + id + "/retry", { method: "POST" }),
  updateWebhookEvents: (events) => req("/api/companies/me/webhook-events", { method: "PUT", body: JSON.stringify({ events }) }),
  listTeam: () => req("/api/companies/me/team"),
  inviteMember: (email, role) => req("/api/companies/me/team/invite", { method: "POST", body: JSON.stringify({ email, role }) }),
  removeMember: (id) => req("/api/companies/me/team/" + id, { method: "DELETE" }),
  changeMemberRole: (id, role) => req("/api/companies/me/team/" + id + "/role", { method: "PUT", body: JSON.stringify({ role }) }),
  resendInvitation: (id) => req("/api/companies/me/team/" + id + "/resend", { method: "POST" }),
  getInvitation: (token) => req("/api/invitations/" + token),
  acceptInvitation: (token) => req("/api/invitations/" + token + "/accept", { method: "POST" }),
  listApiKeys: () => req("/api/companies/me/api-keys"),
  createApiKey: (label, scopes) => req("/api/companies/me/api-keys", { method: "POST", body: JSON.stringify({ label, scopes: scopes ?? null }) }),
  scopesCatalog: () => req("/api/companies/me/api-keys/scopes"),
  revokeApiKey: (id) => req("/api/companies/me/api-keys/" + id, { method: "DELETE" }),
  getApiKeyStats: (id) => req("/api/companies/me/api-keys/" + id + "/stats"),
}
